import React, { useState, useEffect } from 'react';

interface SpineNode {
  id: string;
  label: string;
  x: number;
  ports: number;
  throughput: string;
}

interface LeafNode {
  id: string;
  label: string;
  x: number;
  rack: string;
  servers: number;
}

interface ServerNode {
  id: string;
  parentLeaf: string;
  x: number;
  yOffset: number;
  label: string;
}

interface RouterNode {
  id: string;
  label: string;
  x: number;
  throughput: string;
  asn: string;
}

interface ISPNode {
  id: string;
  label: string;
  x: number;
  tier: string;
  asn: string;
}

interface Connection {
  id: string;
  from: any;
  to: any;
  type: string;
}

const SpineLeafTopology: React.FC = () => {
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);
  const [animationPhase, setAnimationPhase] = useState(0);
  const [showTraffic, setShowTraffic] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setAnimationPhase(1), 200);
    return () => clearTimeout(timer);
  }, []);

  // Memoize connection color/width calculations to prevent recalculation on every render
  const getConnectionStyles = React.useCallback((fromId: string, toId: string) => {
    const isHighlighted = hoveredNode === fromId || hoveredNode === toId;
    const shouldDim = hoveredNode && !isHighlighted;
    return {
      color: isHighlighted ? '#00ff88' : '#2a3a4a',
      width: isHighlighted ? 3 : 1.5,
      opacity: shouldDim ? 0.2 : 0.6,
      glow: isHighlighted
    };
  }, [hoveredNode]);

  const routerNodes: RouterNode[] = [
    { id: 'router-1', label: 'RTR-01', x: 450, throughput: '400G', asn: 'AS64512' },
    { id: 'router-2', label: 'RTR-02', x: 650, throughput: '400G', asn: 'AS64512' },
    { id: 'router-3', label: 'RTR-03', x: 850, throughput: '400G', asn: 'AS64512' },
  ];

  const ispNodes: ISPNode[] = [
    { id: 'isp-1', label: 'ISP-Alpha', x: 350, tier: 'Tier 1', asn: 'AS174' },
    { id: 'isp-2', label: 'ISP-Beta', x: 550, tier: 'Tier 1', asn: 'AS3356' },
    { id: 'isp-3', label: 'ISP-Gamma', x: 750, tier: 'Tier 2', asn: 'AS6939' },
    { id: 'isp-4', label: 'ISP-Delta', x: 950, tier: 'Tier 2', asn: 'AS1299' },
  ];

  const spineNodes: SpineNode[] = [
    { id: 'spine-1', label: 'SPINE-01', x: 350, ports: 32, throughput: '100G' },
    { id: 'spine-2', label: 'SPINE-02', x: 550, ports: 32, throughput: '100G' },
    { id: 'spine-3', label: 'SPINE-03', x: 750, ports: 32, throughput: '100G' },
    { id: 'spine-4', label: 'SPINE-04', x: 950, ports: 32, throughput: '100G' },
  ];

  const leafNodes: LeafNode[] = [
    { id: 'leaf-1', label: 'LEAF-01', x: 125, rack: 'R01', servers: 48 },
    { id: 'leaf-2', label: 'LEAF-02', x: 275, rack: 'R02', servers: 48 },
    { id: 'leaf-3', label: 'LEAF-03', x: 425, rack: 'R03', servers: 48 },
    { id: 'leaf-4', label: 'LEAF-04', x: 575, rack: 'R04', servers: 48 },
    { id: 'leaf-5', label: 'LEAF-05', x: 725, rack: 'R05', servers: 48 },
    { id: 'leaf-6', label: 'LEAF-06', x: 875, rack: 'R06', servers: 48 },
    { id: 'leaf-7', label: 'LEAF-07', x: 1025, rack: 'R07', servers: 48 },
    { id: 'leaf-8', label: 'LEAF-08', x: 1175, rack: 'R08', servers: 48 },
  ];

  const serverNodes: ServerNode[] = leafNodes.flatMap((leaf, leafIndex) =>
    Array.from({ length: 12 }, (_, serverIndex) => ({
      id: `server-${leafIndex}-${serverIndex}`,
      parentLeaf: leaf.id,
      x: leaf.x,
      yOffset: serverIndex * 45,
      label: `SRV-${String(leafIndex * 12 + serverIndex + 1).padStart(3, '0')}`,
    }))
  );

  // Memoize connections to prevent recalculation on every render
  const connections = React.useMemo(() => {
    const conns: Connection[] = [];

    // ISP to Router connections
    ispNodes.forEach(isp => {
      routerNodes.forEach(router => {
        conns.push({
          id: `${isp.id}-${router.id}`,
          from: isp,
          to: router,
          type: 'isp-router'
        });
      });
    });

    // Router to Spine connections (each router connects to all spines)
    routerNodes.forEach(router => {
      spineNodes.forEach(spine => {
        conns.push({
          id: `${router.id}-${spine.id}`,
          from: router,
          to: spine,
          type: 'router-spine'
        });
      });
    });

    // Spine to Leaf connections
    leafNodes.forEach(leaf => {
      spineNodes.forEach(spine => {
        conns.push({
          id: `${leaf.id}-${spine.id}`,
          from: leaf,
          to: spine,
          type: 'spine-leaf'
        });
      });
    });

    return conns;
  }, [ispNodes, routerNodes, spineNodes, leafNodes]);

  // Generate traffic particles only for visible connections - limit to 20 for performance
  const trafficParticles = React.useMemo(() => {
    if (!showTraffic || !hoveredNode) return []; // Only show when hovering

    const particles: Array<{
      id: string;
      x1: number;
      y1: number;
      x2: number;
      y2: number;
      delay: number;
      duration: number;
    }> = [];

    // Only show traffic on hovered connections
    const visibleConns = connections.filter(conn =>
      (conn.type === 'isp-router' || conn.type === 'router-spine' || conn.type === 'spine-leaf')
    );

    const activeConns = visibleConns.filter(conn =>
      conn.from.id === hoveredNode || conn.to.id === hoveredNode
    );

    activeConns.slice(0, 20).forEach((conn, idx) => {
      let y1, y2;
      if (conn.type === 'isp-router') {
        y1 = 130;
        y2 = 180;
      } else if (conn.type === 'router-spine') {
        y1 = 220;
        y2 = 280;
      } else {
        y1 = 470;
        y2 = 330;
      }

      particles.push({
        id: `particle-${conn.id}`,
        x1: conn.from.x,
        y1,
        x2: conn.to.x,
        y2,
        delay: (idx * 0.3) % 2,
        duration: 1.5 + (idx % 3) * 0.5
      });
    });

    return particles;
  }, [connections, hoveredNode, showTraffic]);

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #0a0e14 0%, #0d1219 50%, #0a0e14 100%)',
      fontFamily: '"JetBrains Mono", "Fira Code", "SF Mono", monospace',
      color: '#c4d4e4',
      overflow: 'hidden',
      position: 'relative',
    }}>
      {/* Grid background */}
      <div style={{
        position: 'absolute',
        inset: 0,
        backgroundImage: `
          linear-gradient(rgba(0, 255, 136, 0.03) 1px, transparent 1px),
          linear-gradient(90deg, rgba(0, 255, 136, 0.03) 1px, transparent 1px)
        `,
        backgroundSize: '50px 50px',
        pointerEvents: 'none',
      }} />

      {/* Scan line effect */}
      <div style={{
        position: 'absolute',
        inset: 0,
        background: `repeating-linear-gradient(
          0deg,
          transparent,
          transparent 2px,
          rgba(0, 0, 0, 0.1) 2px,
          rgba(0, 0, 0, 0.1) 4px
        )`,
        pointerEvents: 'none',
        opacity: 0.5,
      }} />

      {/* Header */}
      <header style={{
        padding: '32px 48px',
        borderBottom: '1px solid rgba(0, 255, 136, 0.15)',
        position: 'relative',
        background: 'linear-gradient(180deg, rgba(0, 255, 136, 0.05) 0%, transparent 100%)',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '8px',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
          }}>
            <div style={{
              width: '12px',
              height: '12px',
              background: '#00ff88',
              borderRadius: '50%',
              boxShadow: '0 0 20px #00ff88, 0 0 40px rgba(0, 255, 136, 0.5)',
              animation: 'pulse 2s ease-in-out infinite',
            }} />
            <h1 style={{
              fontSize: '14px',
              fontWeight: 600,
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              color: '#00ff88',
              margin: 0,
              textShadow: '0 0 30px rgba(0, 255, 136, 0.5)',
            }}>
              Network Topology Visualizer
            </h1>
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
          }}>
            <button
              onClick={() => setShowTraffic(!showTraffic)}
              className="traffic-toggle"
              style={{
                background: showTraffic ? 'rgba(0, 255, 136, 0.1)' : 'rgba(90, 122, 138, 0.1)',
                border: `1px solid ${showTraffic ? '#00ff88' : '#3a5a6a'}`,
                color: showTraffic ? '#00ff88' : '#5a7a8a',
                padding: '6px 12px',
                borderRadius: '4px',
                fontSize: '10px',
                fontWeight: 600,
                letterSpacing: '0.1em',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                textTransform: 'uppercase',
                fontFamily: 'inherit',
              }}
            >
              {showTraffic ? '🚀 Traffic On' : '⏸ Traffic Off'}
            </button>
            <div style={{
              fontSize: '11px',
              color: '#5a7a8a',
              letterSpacing: '0.05em',
            }}>
              ⚡ GPU ACCELERATED
            </div>
          </div>
        </div>
        <h2 style={{
          fontSize: '36px',
          fontWeight: 300,
          letterSpacing: '-0.02em',
          color: '#ffffff',
          margin: '8px 0 0 0',
        }}>
          Spine-Leaf Architecture
        </h2>
        <p style={{
          fontSize: '13px',
          color: '#5a7a8a',
          margin: '12px 0 0 0',
          letterSpacing: '0.05em',
        }}>
          DATACENTER.FABRIC.v3.0 • 4 ISPs • 3 ROUTERS • 4 SPINE SWITCHES • 8 LEAF SWITCHES • 96 COMPUTE NODES
        </p>
      </header>

      {/* Main visualization */}
      <div style={{
        padding: '48px',
        display: 'flex',
        gap: '48px',
      }}>
        {/* Topology diagram */}
        <div style={{
          flex: 1,
          background: 'rgba(0, 20, 30, 0.4)',
          border: '1px solid rgba(0, 255, 136, 0.1)',
          borderRadius: '4px',
          padding: '32px',
          position: 'relative',
          overflow: 'hidden',
        }}>
          {/* Corner decorations */}
          <div style={{ position: 'absolute', top: 0, left: 0, width: '20px', height: '20px', borderTop: '2px solid #00ff88', borderLeft: '2px solid #00ff88' }} />
          <div style={{ position: 'absolute', top: 0, right: 0, width: '20px', height: '20px', borderTop: '2px solid #00ff88', borderRight: '2px solid #00ff88' }} />
          <div style={{ position: 'absolute', bottom: 0, left: 0, width: '20px', height: '20px', borderBottom: '2px solid #00ff88', borderLeft: '2px solid #00ff88' }} />
          <div style={{ position: 'absolute', bottom: 0, right: 0, width: '20px', height: '20px', borderBottom: '2px solid #00ff88', borderRight: '2px solid #00ff88' }} />

          <svg
            viewBox="0 0 1300 1100"
            style={{
              width: '100%',
              height: 'auto',
              willChange: hoveredNode ? 'contents' : 'auto',
            }}
          >
            <defs>
              <linearGradient id="ispGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#4a1a3a" />
                <stop offset="100%" stopColor="#2a0d1f" />
              </linearGradient>
              <linearGradient id="routerGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#3a2a4a" />
                <stop offset="100%" stopColor="#1f0d2a" />
              </linearGradient>
              <linearGradient id="spineGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#1a3a4a" />
                <stop offset="100%" stopColor="#0d1f2a" />
              </linearGradient>
              <linearGradient id="leafGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#1a2a3a" />
                <stop offset="100%" stopColor="#0d151f" />
              </linearGradient>
              <linearGradient id="serverGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#1a1a2a" />
                <stop offset="100%" stopColor="#0d0d15" />
              </linearGradient>
              <filter id="glow">
                <feGaussianBlur stdDeviation="2" result="coloredBlur" />
                <feMerge>
                  <feMergeNode in="coloredBlur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
              <filter id="nodeGlow">
                <feDropShadow dx="0" dy="0" stdDeviation="2" floodColor="#00ff88" floodOpacity="0.3" />
              </filter>
            </defs>

            {/* Layer labels */}
            <text x="30" y="50" fill="#3a5a6a" fontSize="11" fontWeight="500" letterSpacing="0.1em">
              ISP LAYER
            </text>
            <text x="30" y="150" fill="#3a5a6a" fontSize="11" fontWeight="500" letterSpacing="0.1em">
              ROUTER LAYER
            </text>
            <text x="30" y="250" fill="#3a5a6a" fontSize="11" fontWeight="500" letterSpacing="0.1em">
              SPINE LAYER
            </text>
            <text x="30" y="420" fill="#3a5a6a" fontSize="11" fontWeight="500" letterSpacing="0.1em">
              LEAF LAYER
            </text>
            <text x="30" y="580" fill="#3a5a6a" fontSize="11" fontWeight="500" letterSpacing="0.1em">
              COMPUTE LAYER
            </text>

            {/* Connection lines - ISP to Router, Router to Spine, Spine to Leaf */}
            <g opacity={animationPhase ? 1 : 0} style={{ transition: 'opacity 0.8s ease-out', willChange: 'opacity' }}>
              {connections.filter(conn => conn.type === 'isp-router' || conn.type === 'router-spine' || conn.type === 'spine-leaf').map((conn) => {
                let y1, y2;
                if (conn.type === 'isp-router') {
                  y1 = 130;  // ISP bottom
                  y2 = 180;  // Router top
                } else if (conn.type === 'router-spine') {
                  y1 = 220;  // Router bottom
                  y2 = 280;  // Spine top
                } else {  // spine-leaf
                  y1 = 470;  // Leaf top
                  y2 = 330;  // Spine bottom
                }
                const styles = getConnectionStyles(conn.from.id, conn.to.id);
                return (
                  <line
                    key={conn.id}
                    x1={conn.from.x}
                    y1={y1}
                    x2={conn.to.x}
                    y2={y2}
                    stroke={styles.color}
                    strokeWidth={styles.width}
                    strokeLinecap="round"
                    opacity={styles.opacity}
                    style={{
                      transition: 'stroke 0.2s ease, stroke-width 0.2s ease, opacity 0.2s ease',
                      willChange: hoveredNode ? 'stroke, stroke-width, opacity' : 'auto',
                    }}
                  />
                );
              })}
            </g>

            {/* Connection lines - Leaf to Servers */}
            <g opacity={animationPhase ? 1 : 0} style={{ transition: 'opacity 1s ease-out 0.3s' }}>
              {serverNodes.map((server) => {
                const isHighlighted = hoveredNode === server.parentLeaf || hoveredNode === server.id;
                return (
                  <line
                    key={`conn-${server.id}`}
                    x1={server.x}
                    y1={510}
                    x2={server.x}
                    y2={630 + server.yOffset}
                    stroke={isHighlighted ? '#00ff88' : '#1a2a3a'}
                    strokeWidth={isHighlighted ? 2 : 1}
                    strokeDasharray={isHighlighted ? 'none' : '4 4'}
                    opacity={hoveredNode && !isHighlighted ? 0.2 : 0.6}
                    style={{ transition: 'all 0.3s ease' }}
                  />
                );
              })}
            </g>

            {/* Traffic particles - GPU accelerated with CSS */}
            {showTraffic && (
              <g className="traffic-layer">
                {trafficParticles.map((particle) => {
                  const dx = particle.x2 - particle.x1;
                  const dy = particle.y2 - particle.y1;

                  return (
                    <g key={particle.id}>
                      <circle
                        cx={particle.x1}
                        cy={particle.y1}
                        r="2.5"
                        fill="#00ff88"
                        className="traffic-particle"
                        style={{
                          animationDuration: `${particle.duration}s`,
                          animationDelay: `${particle.delay}s`,
                          '--tx': `${dx}px`,
                          '--ty': `${dy}px`,
                        } as React.CSSProperties}
                      />
                      {/* Trailing glow effect */}
                      <circle
                        cx={particle.x1}
                        cy={particle.y1}
                        r="4"
                        fill="#00ff88"
                        opacity="0.3"
                        className="traffic-particle-glow"
                        style={{
                          animationDuration: `${particle.duration}s`,
                          animationDelay: `${particle.delay}s`,
                          '--tx': `${dx}px`,
                          '--ty': `${dy}px`,
                        } as React.CSSProperties}
                      />
                    </g>
                  );
                })}
              </g>
            )}

            {/* ISP nodes */}
            <g>
              {ispNodes.map((node) => {
                const isHovered = hoveredNode === node.id;
                return (
                  <g
                    key={node.id}
                    transform={`translate(${node.x}, 100)`}
                    onMouseEnter={() => setHoveredNode(node.id)}
                    onMouseLeave={() => setHoveredNode(null)}
                    style={{ cursor: 'pointer' }}
                    opacity={animationPhase ? 1 : 0}
                  >
                    <rect
                      x="-55"
                      y="-30"
                      width="110"
                      height="60"
                      rx="6"
                      fill="url(#ispGradient)"
                      stroke={isHovered ? '#ff88dd' : '#4a2a5a'}
                      strokeWidth={isHovered ? 2 : 1}
                      filter={isHovered ? 'url(#nodeGlow)' : 'none'}
                      style={{ transition: 'all 0.3s ease' }}
                    />
                    <circle cx="-45" cy="-22" r="3" fill="#ff88dd" className="led-pulse" style={{ animationDelay: '0s' }} />
                    <text
                      x="0"
                      y="-5"
                      textAnchor="middle"
                      fill={isHovered ? '#ff88dd' : '#aa8aba'}
                      fontSize="12"
                      fontWeight="600"
                      letterSpacing="0.05em"
                    >
                      {node.label}
                    </text>
                    <text
                      x="0"
                      y="12"
                      textAnchor="middle"
                      fill="#6a4a7a"
                      fontSize="9"
                      letterSpacing="0.1em"
                    >
                      {node.tier} • {node.asn}
                    </text>
                  </g>
                );
              })}
            </g>

            {/* Router nodes */}
            <g>
              {routerNodes.map((node) => {
                const isHovered = hoveredNode === node.id;
                return (
                  <g
                    key={node.id}
                    transform={`translate(${node.x}, 200)`}
                    onMouseEnter={() => setHoveredNode(node.id)}
                    onMouseLeave={() => setHoveredNode(null)}
                    style={{ cursor: 'pointer' }}
                    opacity={animationPhase ? 1 : 0}
                  >
                    <rect
                      x="-55"
                      y="-30"
                      width="110"
                      height="60"
                      rx="4"
                      fill="url(#routerGradient)"
                      stroke={isHovered ? '#aa88ff' : '#3a2a5a'}
                      strokeWidth={isHovered ? 2 : 1}
                      filter={isHovered ? 'url(#nodeGlow)' : 'none'}
                      style={{ transition: 'all 0.3s ease' }}
                    />
                    <circle cx="-45" cy="-22" r="3" fill="#aa88ff" className="led-pulse" style={{ animationDelay: '0.5s' }} />
                    <text
                      x="0"
                      y="-5"
                      textAnchor="middle"
                      fill={isHovered ? '#aa88ff' : '#9a8aba'}
                      fontSize="12"
                      fontWeight="600"
                      letterSpacing="0.05em"
                    >
                      {node.label}
                    </text>
                    <text
                      x="0"
                      y="12"
                      textAnchor="middle"
                      fill="#5a4a7a"
                      fontSize="9"
                      letterSpacing="0.1em"
                    >
                      {node.throughput} • {node.asn}
                    </text>
                  </g>
                );
              })}
            </g>

            {/* Spine nodes */}
            <g>
              {spineNodes.map((node) => {
                const isHovered = hoveredNode === node.id;
                return (
                  <g
                    key={node.id}
                    transform={`translate(${node.x}, 300)`}
                    onMouseEnter={() => setHoveredNode(node.id)}
                    onMouseLeave={() => setHoveredNode(null)}
                    style={{ cursor: 'pointer' }}
                    opacity={animationPhase ? 1 : 0}
                  >
                    <rect
                      x="-50"
                      y="-30"
                      width="100"
                      height="60"
                      rx="4"
                      fill="url(#spineGradient)"
                      stroke={isHovered ? '#00ff88' : '#2a4a5a'}
                      strokeWidth={isHovered ? 2 : 1}
                      filter={isHovered ? 'url(#nodeGlow)' : 'none'}
                      style={{ transition: 'all 0.3s ease' }}
                    />
                    {/* Status LED */}
                    <circle cx="-42" cy="-22" r="3" fill="#00ff88" className="led-pulse" style={{ animationDelay: '1s' }} />
                    <text
                      x="0"
                      y="-5"
                      textAnchor="middle"
                      fill={isHovered ? '#00ff88' : '#8aaaba'}
                      fontSize="12"
                      fontWeight="600"
                      letterSpacing="0.05em"
                    >
                      {node.label}
                    </text>
                    <text
                      x="0"
                      y="12"
                      textAnchor="middle"
                      fill="#4a6a7a"
                      fontSize="9"
                      letterSpacing="0.1em"
                    >
                      {node.throughput} • {node.ports}P
                    </text>
                  </g>
                );
              })}
            </g>

            {/* Leaf nodes */}
            <g>
              {leafNodes.map((node, i) => {
                const isHovered = hoveredNode === node.id;
                return (
                  <g
                    key={node.id}
                    transform={`translate(${node.x}, 470)`}
                    onMouseEnter={() => setHoveredNode(node.id)}
                    onMouseLeave={() => setHoveredNode(null)}
                    style={{ cursor: 'pointer' }}
                    opacity={animationPhase ? 1 : 0}
                  >
                    <rect
                      x="-45"
                      y="-25"
                      width="90"
                      height="50"
                      rx="4"
                      fill="url(#leafGradient)"
                      stroke={isHovered ? '#00ff88' : '#2a3a4a'}
                      strokeWidth={isHovered ? 2 : 1}
                      filter={isHovered ? 'url(#nodeGlow)' : 'none'}
                      style={{ transition: 'all 0.3s ease' }}
                    />
                    {/* Status LED */}
                    <circle cx="-38" cy="-18" r="3" fill="#00ff88" className="led-pulse" style={{ animationDelay: `${i * 0.3}s` }} />
                    <text
                      x="0"
                      y="-2"
                      textAnchor="middle"
                      fill={isHovered ? '#00ff88' : '#8aaaba'}
                      fontSize="11"
                      fontWeight="600"
                      letterSpacing="0.05em"
                    >
                      {node.label}
                    </text>
                    <text
                      x="0"
                      y="12"
                      textAnchor="middle"
                      fill="#4a6a7a"
                      fontSize="8"
                      letterSpacing="0.1em"
                    >
                      RACK {node.rack}
                    </text>
                  </g>
                );
              })}
            </g>

            {/* Server nodes */}
            <g>
              {serverNodes.map((server, i) => {
                const isHovered = hoveredNode === server.id;
                const parentHovered = hoveredNode === server.parentLeaf;
                return (
                  <g
                    key={server.id}
                    transform={`translate(${server.x}, ${630 + server.yOffset})`}
                    onMouseEnter={() => setHoveredNode(server.id)}
                    onMouseLeave={() => setHoveredNode(null)}
                    style={{ cursor: 'pointer' }}
                    opacity={animationPhase ? 1 : 0}
                  >
                    <rect
                      x="-35"
                      y="-15"
                      width="70"
                      height="30"
                      rx="2"
                      fill="url(#serverGradient)"
                      stroke={isHovered || parentHovered ? '#00ff88' : '#1a2a3a'}
                      strokeWidth={isHovered ? 2 : 1}
                      filter={isHovered ? 'url(#nodeGlow)' : 'none'}
                      style={{ transition: 'all 0.3s ease' }}
                    />
                    {/* Activity indicator */}
                    <rect
                      x="-28"
                      y="-8"
                      width="4"
                      height="4"
                      fill={isHovered ? '#00ff88' : '#2a4a3a'}
                      className="led-pulse-fast"
                      style={{ animationDelay: `${(i * 0.1) % 2}s` }}
                    />
                    <text
                      x="0"
                      y="4"
                      textAnchor="middle"
                      fill={isHovered || parentHovered ? '#00ff88' : '#6a8a9a'}
                      fontSize="9"
                      fontWeight="500"
                      letterSpacing="0.05em"
                    >
                      {server.label}
                    </text>
                  </g>
                );
              })}
            </g>
          </svg>
        </div>

        {/* Info panel */}
        <div style={{
          width: '320px',
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
        }}>
          {/* Architecture info */}
          <div style={{
            background: 'rgba(0, 20, 30, 0.4)',
            border: '1px solid rgba(0, 255, 136, 0.1)',
            borderRadius: '4px',
            padding: '24px',
          }}>
            <h3 style={{
              fontSize: '11px',
              fontWeight: 600,
              letterSpacing: '0.15em',
              color: '#00ff88',
              margin: '0 0 16px 0',
              textTransform: 'uppercase',
            }}>
              Architecture Overview
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {[
                { label: 'Topology', value: 'Spine-Leaf (Clos)' },
                { label: 'Redundancy', value: 'Full Mesh' },
                { label: 'Oversubscription', value: '3:1' },
                { label: 'East-West BW', value: '1.6 Tbps' },
                { label: 'Latency', value: '< 2μs' },
              ].map((item, i) => (
                <div key={i} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '8px 12px',
                  background: 'rgba(0, 40, 50, 0.3)',
                  borderRadius: '2px',
                  borderLeft: '2px solid #00ff88',
                }}>
                  <span style={{ color: '#5a7a8a', fontSize: '12px' }}>{item.label}</span>
                  <span style={{ color: '#c4d4e4', fontSize: '12px', fontWeight: 500 }}>{item.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Benefits */}
          <div style={{
            background: 'rgba(0, 20, 30, 0.4)',
            border: '1px solid rgba(0, 255, 136, 0.1)',
            borderRadius: '4px',
            padding: '24px',
          }}>
            <h3 style={{
              fontSize: '11px',
              fontWeight: 600,
              letterSpacing: '0.15em',
              color: '#00ff88',
              margin: '0 0 16px 0',
              textTransform: 'uppercase',
            }}>
              Key Benefits
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {[
                { icon: '◈', title: 'Scalability', desc: 'Add spine/leaf switches without disruption' },
                { icon: '◇', title: 'Low Latency', desc: 'Maximum 2 hops between any endpoints' },
                { icon: '○', title: 'High Bandwidth', desc: 'Equal bandwidth across all paths' },
                { icon: '□', title: 'Resilience', desc: 'No single point of failure in fabric' },
              ].map((item, i) => (
                <div key={i} style={{
                  padding: '12px',
                  background: 'rgba(0, 40, 50, 0.2)',
                  borderRadius: '2px',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <span style={{ color: '#00ff88', fontSize: '14px' }}>{item.icon}</span>
                    <span style={{ color: '#c4d4e4', fontSize: '12px', fontWeight: 600 }}>{item.title}</span>
                  </div>
                  <p style={{ color: '#5a7a8a', fontSize: '11px', margin: 0, lineHeight: 1.5 }}>
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Legend */}
          <div style={{
            background: 'rgba(0, 20, 30, 0.4)',
            border: '1px solid rgba(0, 255, 136, 0.1)',
            borderRadius: '4px',
            padding: '24px',
          }}>
            <h3 style={{
              fontSize: '11px',
              fontWeight: 600,
              letterSpacing: '0.15em',
              color: '#00ff88',
              margin: '0 0 16px 0',
              textTransform: 'uppercase',
            }}>
              Legend
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {[
                { color: '#4a1a3a', label: 'ISP', desc: 'Tier 1/2 Provider' },
                { color: '#3a2a4a', label: 'Router', desc: 'Edge / BGP' },
                { color: '#1a3a4a', label: 'Spine Switch', desc: '100G Uplinks' },
                { color: '#1a2a3a', label: 'Leaf Switch', desc: 'ToR / Access' },
                { color: '#1a1a2a', label: 'Server Node', desc: 'Compute' },
              ].map((item, i) => (
                <div key={i} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                }}>
                  <div style={{
                    width: '24px',
                    height: '16px',
                    background: item.color,
                    border: '1px solid #2a4a5a',
                    borderRadius: '2px',
                  }} />
                  <div>
                    <span style={{ color: '#8aaaba', fontSize: '11px', fontWeight: 500 }}>{item.label}</span>
                    <span style={{ color: '#4a6a7a', fontSize: '10px', marginLeft: '8px' }}>{item.desc}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer style={{
        padding: '24px 48px',
        borderTop: '1px solid rgba(0, 255, 136, 0.1)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <div style={{
          fontSize: '10px',
          color: '#3a5a6a',
          letterSpacing: '0.1em',
        }}>
          INTERACTIVE TOPOLOGY • HOVER TO EXPLORE CONNECTIONS
        </div>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
          <div style={{
            width: '8px',
            height: '8px',
            background: '#00ff88',
            borderRadius: '50%',
            animation: 'pulse 6s ease-in-out infinite',
          }} />
          <span style={{ fontSize: '10px', color: '#00ff88', letterSpacing: '0.1em' }}>
            ALL SYSTEMS OPERATIONAL
          </span>
        </div>
      </footer>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@300;400;500;600&display=swap');

        @keyframes pulse {
          0%, 100% { opacity: 1; box-shadow: 0 0 20px currentColor; }
          50% { opacity: 0.6; box-shadow: 0 0 10px currentColor; }
        }

        /* GPU-accelerated LED pulse animations */
        @keyframes led-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }

        @keyframes led-pulse-fast {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }

        .led-pulse {
          animation: led-pulse 4s ease-in-out infinite;
          will-change: opacity;
          transform: translateZ(0);
        }

        .led-pulse-fast {
          animation: led-pulse-fast 1.5s ease-in-out infinite;
          will-change: opacity;
          transform: translateZ(0);
        }

        /* Traffic particle animations - GPU accelerated */
        @keyframes traffic-flow {
          0% {
            transform: translate(0, 0);
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          90% {
            opacity: 1;
          }
          100% {
            transform: translate(var(--tx), var(--ty));
            opacity: 0;
          }
        }

        .traffic-particle {
          animation: traffic-flow linear infinite;
          will-change: transform, opacity;
          transform: translateZ(0);
          filter: drop-shadow(0 0 3px #00ff88);
        }

        .traffic-particle-glow {
          animation: traffic-flow linear infinite;
          will-change: transform, opacity;
          transform: translateZ(0);
        }

        .traffic-layer {
          pointer-events: none;
        }

        /* Traffic toggle button hover */
        .traffic-toggle:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 255, 136, 0.2);
        }

        .traffic-toggle:active {
          transform: translateY(0);
        }

        * {
          box-sizing: border-box;
        }

        html, body, #root {
          margin: 0;
          padding: 0;
          width: 100%;
          height: 100%;
        }

        /* Force GPU acceleration for SVG */
        svg {
          transform: translateZ(0);
          backface-visibility: hidden;
          perspective: 1000px;
        }

        svg * {
          vector-effect: non-scaling-stroke;
        }

        /* Optimize node rendering */
        svg g {
          will-change: transform;
        }

        /* Reduce paint area on hover */
        svg rect, svg circle, svg text {
          pointer-events: bounding-box;
        }
      `}</style>
    </div>
  );
};

export default SpineLeafTopology;
